import { FormlyFieldConfig } from '@ngx-formly/core';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  getAllowedDaysOfWeekValidatorFormConfig,
  getAllowedFileFormatsValidatorFormConfig,
  getFileSizeValidatorFormConfig,
  getIdConditionsValidatorFormConfig,
  getMaxDateConfigValidatorFormConfig,
  getMaxLengthValidatorFormConfig,
  getMaxValidatorFormConfig,
  getMinDateConfigValidatorFormConfig,
  getMinLengthValidatorFormConfig,
  getMinValidatorFormConfig,
  getPatternValidatorFormConfig,
  getRequiredValidatorFormConfig,
} from './form-fields-formly-validation-field-generator.util';
import { FormBuilderService } from 'src/app/core/services/form-builder.service';
import { ValidationTypeDefaultMessage } from '../form-builder-validation-type-message.utils';
import { EIremboFormlyValidationTypes } from '@irembo-andela/irembogov3-common';
import { SettersConfigurationUtils } from '../form-field-formly-types-main-configurations/form-field-formly-configuration-classes/utils/setters';
import { generateDataTableSelectValidationFields } from '../form-field-formly-types-main-configurations/data-table-field-type.configurator';

/**
 * this interface defined the structure that defines the configurations
 * for defining the form, setter function and parser finctions for validations.
 */
export interface IFormlyFieldValidationFormsConfig {
  label: string;
  limitNumberOfValidations?: boolean; // Optional; applies to required as validations can only be true or false; hense they cant add more validaition logics
  formlyConfig: (
    formBuilderServiceInstance?: FormBuilderService
  ) => FormlyFieldConfig;
  setter: (
    model: IFormFieldsValidationModel,
    field: FormlyFieldConfig
  ) => FormlyFieldConfig;
  defaultErrorMesage?: string;
}

/**
 * This would be used by helper functions in the classes to help get their defined
 * validation types form, setter function and parser function.
 */
export const FORMLY_FIELDS_VALIDATION_FORMS_CONFIG: Record<
  any,
  IFormlyFieldValidationFormsConfig
> = {
  required: {
    label: 'Required',
    limitNumberOfValidations: true,
    formlyConfig: () => getRequiredValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setRequiredValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.required
    ),
  },
  min: {
    label: 'Minimum',
    limitNumberOfValidations: false,
    formlyConfig: () => getMinValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMinValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.min
    ),
  },
  max: {
    label: 'Maximum',
    limitNumberOfValidations: false,
    formlyConfig: () => getMaxValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMaxValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.max
    ),
  },
  minLength: {
    label: 'Minimum Length',
    limitNumberOfValidations: false,
    formlyConfig: () => getMinLengthValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMinLengthValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.minLength
    ),
  },
  maxLength: {
    label: 'Maximum Length',
    limitNumberOfValidations: false,
    formlyConfig: () => getMaxLengthValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMaxLengthValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.maxLength
    ),
  },
  patternValidation: {
    label: 'Pattern',
    limitNumberOfValidations: false,
    formlyConfig: () => getPatternValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setPatternValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.pattern
    ),
  },
  idConditions: {
    label: 'Age Limit',
    limitNumberOfValidations: true,
    formlyConfig: () => getIdConditionsValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setIdConditionsValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidId
    ),
  },
  invalidInput: {
    label: 'Invalid Input',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidInput
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidInput
    ),
  },
  invalidId: {
    label: 'Invalid ID',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidId
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidId
    ),
  },
  invalidNumber: {
    label: 'Invalid Phone Number',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidNumber
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidNumber
    ),
  },
  invalidNumberFormat: {
    label: 'Invalid Phone Number Format',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidNumberFormat
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidNumberFormat
    ),
  },
  minDateConfig: {
    label: 'Minimum Date',
    limitNumberOfValidations: true,
    formlyConfig: () => getMinDateConfigValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMinDateConfigValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.minDate
    ),
  },
  maxDateConfig: {
    label: 'Maximum Date',
    limitNumberOfValidations: true,
    formlyConfig: () => getMaxDateConfigValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setMaxDateConfigValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.maxDate
    ),
  },
  allowedDaysOfTheWeek: {
    label: 'Allowed Days of the Week',
    limitNumberOfValidations: true,
    formlyConfig: () => getAllowedDaysOfWeekValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setAllowedDaysOfTheWeekValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
    ),
  },
  fileSize: {
    label: 'File Size',
    limitNumberOfValidations: false,
    formlyConfig: () => getFileSizeValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setFileSizeValidatorFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: 'File size not met',
  },
  fileFormats: {
    label: 'File Formats',
    limitNumberOfValidations: true,
    formlyConfig: () => getAllowedFileFormatsValidatorFormConfig(),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setAllowedFileFormatsValidatorFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidfileformat
    ),
  },
  invalidTIN: {
    label: 'Invalid TIN',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidTIN
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidTIN
    ),
  },
  invalidAppNo: {
    label: 'Invalid Application Number',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidAppNo
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidAppNo
    ),
  },
  tableValidation: {
    label: 'Table Selection',
    limitNumberOfValidations: false,
    formlyConfig: (formBuilderServiceInstance?: FormBuilderService) =>
      formBuilderServiceInstance !== undefined
        ? generateDataTableSelectValidationFields(formBuilderServiceInstance)
        : {},
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setTableSelectionValidatorUpdateFieldFromModel(
        model,
        field
      ),
    defaultErrorMesage: 'Table selection validation not met',
  },
  invalidDate: {
    label: 'Invalid Date',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidDate
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidDate
    ),
  },
  minDate: {
    label: 'Minimum Date',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.minDate
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.minDate
    ),
  },
  maxDate: {
    label: 'Maximum Date',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.maxDate
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.maxDate
    ),
  },
  unavailableSlots: {
    label: 'Unavailable Slots',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.unavailableSlots
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.unavailableSlots
    ),
  },
  dayOfWeekIsNotAllowed: {
    label: 'Day of the Week is Not Allowed',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.dayOfWeekIsNotAllowed
    ),
  },
  dateAndTimeValuesRequired: {
    label: 'Date and Time Values Required',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.dateAndTimeValuesRequired
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.dateAndTimeValuesRequired
    ),
  },
  invalidTimeValue: {
    label: 'Invalid Time Value',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidTimeValue
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidTimeValue
    ),
  },
  invalidDateTimeFormat: {
    label: 'Invalid Date Time Format',
    limitNumberOfValidations: true,
    formlyConfig: () => ({}),
    setter: (model: IFormFieldsValidationModel, field: FormlyFieldConfig) =>
      SettersConfigurationUtils.setCommonValidatorUpdateFieldFromModel(
        model,
        field,
        EIremboFormlyValidationTypes.invalidDateTimeFormat
      ),
    defaultErrorMesage: ValidationTypeDefaultMessage(
      EIremboFormlyValidationTypes.invalidDateTimeFormat
    ),
  },
  // populates: undefined,
  // uniqueFieldMessage: undefined,
  // eventCode: undefined,
  // eventDatesMaxSize: undefined,
  // dateTimePickerSetting: undefined,
};
